import React, { useMemo, useState } from "react"
import { Col, Input, Label, Spinner } from "reactstrap"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import TranslatedFileInput from "components/Shared/TranslatedFileInput"
import { getImagePreviewSrc2 } from "components/Shared/fileHandlers"
import { uploadWebsiteBuilderAsset } from "modules/website-builder/services/api"
import {
  useWebsiteBuilderTenantId,
  useWebsiteBuilderType,
} from "modules/website-builder/WebsiteBuilderTenantContext"
import { showAlert } from "store/reducers/alertReducer"

const DEFAULT_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"

const ImageUrlField = ({
  label,
  value = "",
  onChange,
  t,
  md = 6,
  accept = DEFAULT_ACCEPT,
  maxSizeKB = 2048,
  purpose = "",
  compact = false,
}) => {
  const dispatch = useDispatch()
  const { t: translate } = useTranslation()
  const tr = t || translate
  const websiteBuilderTenantId = useWebsiteBuilderTenantId()
  const websiteBuilderType = useWebsiteBuilderType()
  const [sourceType, setSourceType] = useState("URL")
  const [uploading, setUploading] = useState(false)

  const previewSrc = useMemo(() => getImagePreviewSrc2(value || null), [value])

  const handleSourceChange = nextType => {
    setSourceType(nextType)
  }

  const handleUrlChange = event => {
    onChange(event.target.value)
  }

  const handleFileSelected = async event => {
    const fileInput = event.currentTarget
    const file = fileInput.files?.[0]
    if (!file) return

    if (maxSizeKB && file.size > maxSizeKB * 1024) {
      dispatch(
        showAlert({
          text: tr("File size should be less or equal to {{maxSizeKB}}KB", { maxSizeKB }),
          type: "error",
        })
      )
      fileInput.value = ""
      return
    }

    setUploading(true)
    try {
      if (!websiteBuilderTenantId) {
        dispatch(
          showAlert({
            text: tr("Select a tenant before uploading images."),
            type: "error",
          })
        )
        return
      }

      const response = await uploadWebsiteBuilderAsset(
        websiteBuilderTenantId,
        file,
        purpose,
        websiteBuilderType
      )
      if (response?.status && response?.data?.url) {
        onChange(response.data.url)
        dispatch(
          showAlert({
            text: tr("Image uploaded successfully"),
            type: "success",
          })
        )
      } else {
        dispatch(
          showAlert({
            text: tr("Unable to upload image"),
            type: "error",
          })
        )
      }
    } catch (_err) {
      dispatch(
        showAlert({
          text: tr("Unable to upload image"),
          type: "error",
        })
      )
    } finally {
      setUploading(false)
      fileInput.value = ""
    }
  }

  return (
    <Col md={md}>
      <div className={`mb-3 ${compact ? "mb-2" : ""}`}>
        <Label>{tr(label)}</Label>
        <Input
          type="select"
          className="mb-2"
          value={sourceType}
          onChange={e => handleSourceChange(e.target.value)}
        >
          <option value="URL">{tr("Image Url")}</option>
          <option value="UPLOAD">{tr("Upload Image")}</option>
        </Input>

        {sourceType === "URL" ? (
          <Input type="text" value={value || ""} onChange={handleUrlChange} placeholder="https://..." />
        ) : (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <TranslatedFileInput
              accept={accept}
              disabled={uploading}
              onChange={handleFileSelected}
            />
            {uploading ? <Spinner size="sm" color="primary" /> : null}
          </div>
        )}

        {previewSrc ? (
          <div className="mt-2">
            <img
              src={previewSrc}
              alt={tr(label)}
              style={{
                maxWidth: compact ? 72 : 160,
                maxHeight: compact ? 72 : 120,
                objectFit: "contain",
                border: "1px solid #dee2e6",
                borderRadius: 8,
                padding: 4,
                background: "#fff",
              }}
            />
          </div>
        ) : null}
      </div>
    </Col>
  )
}

export default ImageUrlField
